<template>
  <div class="checkin">
    <div v-if="!user" class="checkin-state">
      <p>{{ copy.signInHint }}</p>
      <button class="primary-button" @click="showLoginModal">{{ copy.signIn }}</button>
    </div>
    <div v-else-if="loading" class="checkin-state" role="status">{{ t('checkIn.loading') }}</div>
    <div v-else-if="error" class="checkin-state" role="alert">
      <p>{{ copy.loadError }}</p>
      <button class="primary-button" @click="subscribe">{{ t('checkIn.retry') }}</button>
    </div>

    <template v-else>
      <!-- 首屏：环里是连续天数，环本身是今天的打卡进度；下面是本周七天和继续练习 -->
      <header class="streak-hero">
        <div
          class="streak-ring"
          :class="{ lit: progress.checkedToday, ignite: igniting }"
          role="progressbar"
          :aria-label="copy.todayGoal"
          :aria-valuenow="Math.min(progress.todaySeconds, CHECK_IN_SECONDS)"
          :aria-valuemin="0"
          :aria-valuemax="CHECK_IN_SECONDS"
          :aria-valuetext="todayStatus"
        >
          <svg class="ring" viewBox="0 0 120 120" aria-hidden="true">
            <circle class="ring-track" cx="60" cy="60" r="54" />
            <circle class="ring-fill" cx="60" cy="60" r="54" :style="{ strokeDashoffset: ringOffset }" />
          </svg>
          <StreakFlame class="streak-flame" :lit="progress.checkedToday" :ignite="igniting" />
          <strong class="streak-count">{{ progress.streak }}</strong>
          <span class="streak-unit">{{ copy.streak }}</span>
        </div>
        <p class="today-status" :class="{ complete: progress.checkedToday }" aria-live="polite">{{ todayStatus }}</p>
        <p class="streak-message">{{ streakMessage }}</p>
      </header>

      <ol class="week-track" :aria-label="copy.thisWeek">
        <li
          v-for="(day, index) in progress.week"
          :key="day.key"
          :class="{ complete: day.complete, frozen: day.frozen, today: day.today, future: day.future }"
          :aria-label="`${day.key}: ${day.complete ? copy.checked : day.frozen ? copy.frozenDay : day.future ? copy.upcoming : copy.notChecked}`"
        >
          <span class="week-name">{{ copy.weekdays[index] }}</span>
          <span class="week-mark">
            <span v-if="day.complete" class="material-icons" aria-hidden="true">check</span>
            <span v-else-if="day.frozen" class="material-icons" aria-hidden="true">ac_unit</span>
            <template v-else>{{ day.date.getDate() }}</template>
          </span>
        </li>
      </ol>

      <div class="practice-action">
        <button class="primary-button practice-button" :disabled="opening" @click="practice">
          <span class="material-icons" aria-hidden="true">play_arrow</span>
          <span class="practice-label">{{ lastSource ? copy.resume(lastSource.audioName) : progress.checkedToday ? copy.continue : copy.start }}</span>
        </button>
        <p v-if="openError" class="practice-error" role="alert">{{ openError }}</p>
      </div>

      <section class="panel milestones">
        <div class="panel-head">
          <h2>{{ copy.milestoneTitle }}</h2>
          <span class="panel-meta">{{ copy.best(progress.best) }}</span>
        </div>
        <div class="meter" aria-hidden="true"><span :style="{ width: `${progress.streak / progress.nextMilestone * 100}%` }"></span></div>
        <p class="milestone-next">{{ milestoneNext }}</p>
        <ol class="badges">
          <li v-for="(days, index) in STREAK_MILESTONES" :key="days" :class="{ earned: progress.best >= days }">
            <span class="badge-art">
              <MilestoneBadge :index="index" :earned="progress.best >= days" :next="progress.best < days && days === progress.nextMilestone" />
              <span class="badge-days">{{ copy.dayCount(days) }}</span>
            </span>
            <span class="badge-name">{{ copy.badges[index] }}</span>
            <span class="badge-reward">{{ copy.credits(MILESTONE_REWARDS[index]) }}</span>
          </li>
        </ol>
      </section>

      <WeeklyLeaderboard />

      <section class="panel calendar">
        <div class="panel-head">
          <h2>{{ monthLabel }}</h2>
          <div class="month-nav">
            <button class="icon-button" :aria-label="copy.previous" @click="moveMonth(-1)"><span class="material-icons" aria-hidden="true">chevron_left</span></button>
            <button class="icon-button" :aria-label="copy.next" :disabled="isCurrentMonth" @click="moveMonth(1)"><span class="material-icons" aria-hidden="true">chevron_right</span></button>
          </div>
        </div>
        <p class="month-summary">
          <span>{{ copy.checkinDays }} <strong>{{ monthDays }} {{ copy.days }}</strong></span>
          <span>{{ copy.monthTime }} <strong>{{ formatTime(monthTotal) }}</strong></span>
          <!-- 冻结卡：持有的格子点亮；补上的日子在下面月历里显示成蓝色 -->
          <span class="freeze-held">
            {{ copy.freezeTitle }}
            <span class="freeze-slots" role="img" :aria-label="copy.freezeHeld(progress.freezes)">
              <span v-for="slot in FREEZE_LIMIT" :key="slot" class="material-icons" :class="{ held: slot <= progress.freezes }" aria-hidden="true">ac_unit</span>
            </span>
          </span>
        </p>
        <div class="calendar-grid calendar-weekdays" aria-hidden="true"><span v-for="day in copy.weekdays" :key="day">{{ day }}</span></div>
        <div class="calendar-grid">
          <button
            v-for="day in calendar"
            :key="day.key"
            type="button"
            class="calendar-day"
            :class="[`heat-${heatLevel(day.seconds)}`, { outside: !day.inMonth, frozen: progress.frozen.has(day.key), today: day.key === progress.today, selected: day.key === selectedDay }]"
            :disabled="day.key > progress.today"
            :aria-pressed="day.key === selectedDay"
            :aria-label="`${day.key}: ${progress.frozen.has(day.key) ? copy.frozenDay : formatTime(day.seconds)}`"
            @click="selectedDay = day.key"
          >{{ day.date.getDate() }}</button>
        </div>
        <!-- 点哪天，这里就显示哪天的时长 -->
        <p class="day-detail" aria-live="polite">
          <span>{{ selectedLabel }}</span>
          <strong>{{ formatTime(selectedSeconds) }}</strong>
          <span v-if="selectedSeconds >= CHECK_IN_SECONDS" class="day-checked">{{ copy.checked }}</span>
          <span v-else-if="progress.frozen.has(selectedDay)" class="day-frozen">{{ copy.frozenDay }}</span>
        </p>
      </section>

      <p class="footnote">{{ copy.rule }} {{ copy.freezeRule }} {{ copy.badgeNote }}</p>
    </template>
  </div>
</template>

<script>
// 模块级：组件重新挂载也记得今天已经燃过
let ignitedDay = null
</script>

<script setup>
import WeeklyLeaderboard from './WeeklyLeaderboard.vue'
import StreakFlame from './StreakFlame.vue'
import MilestoneBadge from './MilestoneBadge.vue'
import { computed, onMounted, onUnmounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { doc, onSnapshot } from 'firebase/firestore'
import { db } from '@/firebase'
import { useAuth } from '@/composables/auth'
import { useViews } from '@/composables/views'
import { useModals } from '@/composables/modals'
import { checkInReward, flushListeningActivity } from '@/utils/listeningActivity'
import { readStudySource } from '@/utils/lastStudySource'
import { StudySourceMissingError, resolveStudySource } from '@/composables/studySource'
import { CHECK_IN_SECONDS, DAILY_REWARD, FREEZE_EVERY, FREEZE_LIMIT, MILESTONE_REWARDS, STREAK_MILESTONES, localStudyDate, shiftStudyDate, studyProgress } from '@/utils/studyProgress'

const { t, locale } = useI18n()
const { user } = useAuth()
const { handleViewChange } = useViews()
const { showLoginModal } = useModals()
const now = ref(new Date())
const currentMonth = ref(new Date(now.value.getFullYear(), now.value.getMonth(), 1))
const daily = ref({})
const checkIn = ref({})
const loading = ref(true)
const error = ref(false)
let unsubscribe
let clock
const progress = computed(() => studyProgress(daily.value, now.value, checkIn.value))
// 刚在播放页打完卡回到这里：火焰重新燃一次。每天只燃一次，离开再回来不重复
const igniting = ref(false)
watch(() => loading.value || checkInReward.value?.day !== progress.value.today || ignitedDay === progress.value.today, settled => {
  if (settled) return
  ignitedDay = progress.value.today
  igniting.value = true
  setTimeout(() => { igniting.value = false }, 1200)
}, { immediate: true })
// 月历里选中的那天，默认是今天
const selectedDay = ref(localStudyDate(now.value))
const selectedSeconds = computed(() => progress.value.daily[selectedDay.value] || 0)
const selectedLabel = computed(() => new Date(`${selectedDay.value}T12:00:00`).toLocaleDateString(
  locale.value === 'zh' ? 'zh-CN' : 'en-US', { month: 'long', day: 'numeric', weekday: 'short' }
))
// 热力图分五档：没听、不足 5 分钟（未打卡）、5–15 分钟、15–30 分钟、30 分钟以上
function heatLevel(seconds) {
  if (seconds <= 0) return 0
  if (seconds < CHECK_IN_SECONDS) return 1
  if (seconds < 900) return 2
  return seconds < 1800 ? 3 : 4
}
// 后端发放今天的打卡奖励后写在用户记录里
const todayReward = computed(() => checkIn.value.lastReward?.day === progress.value.today ? checkIn.value.lastReward : null)
const copy = computed(() => locale.value === 'zh' ? {
  signInHint: '登录后查看你的连续学习记录。', signIn: '登录', loadError: '学习记录暂时无法加载，请重试。',
  streak: '天连续打卡', days: '天', dayCount: n => `${n} 天`, best: n => `历史最长连续 ${n} 天`, credits: n => `+${n} 额度`, todayGoal: '今日打卡',
  todayPending: (heard, left, n) => `今天已听 ${heard}，再听 ${left} 打卡，领 ${n} 额度`, todayDone: (heard, n) => `今日已打卡，+${n} 额度已到账 · 今天共听 ${heard}`,
  resume: name => `继续练：${name}`, openFailed: '上次的内容打不开了，先去素材库挑一条吧。',
  
  checked: '已打卡', continue: '继续练习', start: '开始练习',
  rule: '每天累计播放满 5 分钟自动打卡。按本地日期统计，暂停不计时。奖励计入本月额度。',
  frozenDay: '冻结卡补上', freezeTitle: '冻结卡', freezeHeld: n => `持有 ${n} 张冻结卡，最多 ${FREEZE_LIMIT} 张`,
  freezeRule: `连续打卡每满 ${FREEZE_EVERY} 天得 1 张冻结卡，最多存 ${FREEZE_LIMIT} 张；漏打卡的日子会在下次打卡时自动用卡补上，卡不够补齐就不用，连续天数重新开始。`,
  freezeProtecting: n => `漏了 ${n} 天，今天打卡时会自动用掉 ${n} 张冻结卡补上，连续记录不会断。`,
  freezeUsedToday: n => `今天打卡时用掉 ${n} 张冻结卡，补上了漏打卡的日子，连续记录保住了。`,
  freezeEarnedToday: `连续满 ${FREEZE_EVERY} 天，你获得了 1 张冻结卡。哪天漏打卡，下次打卡时会自动补上。`,
  thisWeek: '本周', upcoming: '尚未到来', notChecked: '未打卡', weekdays: ['一', '二', '三', '四', '五', '六', '日'],
  previous: '上个月', next: '下个月', monthTime: '播放时长', checkinDays: '打卡', 
  milestoneTitle: '连续打卡里程碑',
  milestoneNext: (streak, left, name, reward) => `已连续 ${streak} 天，再打卡 ${left} 天点亮「${name}」，奖励 ${reward} 额度。`,
  milestoneAgain: (streak, left, n) => `已连续 ${streak} 天，再打卡 ${left} 天回到连续 ${n} 天。`,
  badges: ['迈出第一步', '一周的坚持', '渐入佳境', '一个月的积累', '让习惯生根', '百日同行'], 
  badgeNote: '第一次达到每个里程碑时，额外获得徽章下标注的额度；徽章按历史最长连续记录点亮，中断后仍然保留。',
  streakDone: '今天已接上。明天，继续为自己留一点学习时间。', streakWaiting: '昨天的坚持还在，今天听满 5 分钟就能接上。', restart: '重新开始也算进步，过去的积累一直都在。', first: '从今天开始，点亮你的第一天。'
} : {
  signInHint: 'Sign in to see your learning streak.', signIn: 'Sign in', loadError: 'Your learning record could not be loaded. Please retry.',
  streak: 'day streak', days: 'days', dayCount: n => `${n} days`, best: n => `Longest streak: ${n} days`, credits: n => `+${n} credits`, todayGoal: 'Today’s check-in',
  todayPending: (heard, left, n) => `${heard} today. ${left} more to check in and earn ${n} credits`, todayDone: (heard, n) => `Checked in, +${n} credits added · ${heard} today`,
  resume: name => `Continue: ${name}`, openFailed: 'Your last item could not be opened. Pick one from the library.',
  
  checked: 'Done', continue: 'Continue practicing', start: 'Start practicing',
  rule: 'Five minutes of playback a day earns a check-in. Uses your local date; paused time does not count. Rewards are added to this month’s credits.',
  frozenDay: 'Covered by a freeze', freezeTitle: 'Freezes', freezeHeld: n => `${n} streak freezes held, up to ${FREEZE_LIMIT}`,
  freezeRule: `Every ${FREEZE_EVERY} days in a row earns a freeze, and you can hold up to ${FREEZE_LIMIT}. Missed days are covered automatically the next time you check in. If you do not have enough freezes to cover them all, none are used and the streak starts over.`,
  freezeProtecting: n => `You missed ${n} day${n > 1 ? 's' : ''}. Checking in today uses ${n} freeze${n > 1 ? 's' : ''} to keep your streak.`,
  freezeUsedToday: n => `Today's check-in used ${n} streak freeze${n > 1 ? 's' : ''} to cover the missed days. Your streak is safe.`,
  freezeEarnedToday: `${FREEZE_EVERY} days in a row earned you a streak freeze. If you miss a day, your next check-in covers it automatically.`,
  thisWeek: 'This week', upcoming: 'Upcoming', notChecked: 'Not yet', weekdays: ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'],
  previous: 'Previous month', next: 'Next month', monthTime: 'Listening', checkinDays: 'Check-ins', 
  milestoneTitle: 'Streak milestones',
  milestoneNext: (streak, left, name, reward) => `${streak}-day streak so far. Check in ${left} more day${left > 1 ? 's' : ''} to unlock “${name}” and ${reward} credits.`,
  milestoneAgain: (streak, left, n) => `${streak}-day streak so far. Check in ${left} more day${left > 1 ? 's' : ''} to get back to ${n} days in a row.`,
  badges: ['First steps', 'One steady week', 'Finding your rhythm', 'A month of progress', 'Growing roots', 'One hundred days'], 
  badgeNote: 'The first time you reach a milestone you earn the credits shown under its badge. Badges use your longest streak and stay earned even after a break.',
  streakDone: 'Today is in the books. Make a little time for yourself tomorrow.', streakWaiting: 'Your streak is still here. Listen for 5 minutes today to extend it.', restart: 'A fresh start counts too. Your past progress is still here.', first: 'Make today the first day of your streak.'
})
const todayStatus = computed(() => {
  const heard = formatTime(progress.value.todaySeconds)
  if (progress.value.checkedToday) return copy.value.todayDone(heard, todayReward.value ? todayReward.value.daily + todayReward.value.bonus : DAILY_REWARD)
  return copy.value.todayPending(heard, formatTime(CHECK_IN_SECONDS - progress.value.todaySeconds), DAILY_REWARD)
})
// 进度环周长 2πr，r = 54
const RING_LENGTH = 2 * Math.PI * 54
const ringOffset = computed(() => RING_LENGTH * (1 - Math.min(1, progress.value.todaySeconds / CHECK_IN_SECONDS)))
// 里程碑面板的下一步：还没点亮的徽章写出名字和奖励；已经点亮过的（奖励只发一次）或 100 天以后，只说还差几天
const milestoneNext = computed(() => {
  const { streak, best, nextMilestone } = progress.value
  const index = STREAK_MILESTONES.indexOf(nextMilestone)
  const left = nextMilestone - streak
  if (index < 0 || best >= nextMilestone) return copy.value.milestoneAgain(streak, left, nextMilestone)
  return copy.value.milestoneNext(streak, left, copy.value.badges[index], MILESTONE_REWARDS[index])
})
// 今天打卡时拿到或用掉冻结卡，首屏优先告诉用户
const streakMessage = computed(() => todayReward.value?.freezesUsed ? copy.value.freezeUsedToday(todayReward.value.freezesUsed) : todayReward.value?.freezeEarned ? copy.value.freezeEarnedToday : progress.value.checkedToday ? copy.value.streakDone : progress.value.protectedDays ? copy.value.freezeProtecting(progress.value.protectedDays) : progress.value.streak ? copy.value.streakWaiting : progress.value.best ? copy.value.restart : copy.value.first)
const monthLabel = computed(() => currentMonth.value.toLocaleDateString(locale.value === 'zh' ? 'zh-CN' : 'en-US', { year: 'numeric', month: 'long' }))
const monthPrefix = computed(() => localStudyDate(currentMonth.value).slice(0, 7))
const monthEntries = computed(() => Object.entries(progress.value.daily).filter(([key]) => key.startsWith(monthPrefix.value)))
const monthTotal = computed(() => monthEntries.value.reduce((sum, [, seconds]) => sum + seconds, 0))
const monthDays = computed(() => monthEntries.value.filter(([, seconds]) => seconds >= CHECK_IN_SECONDS).length)
const isCurrentMonth = computed(() => monthPrefix.value === localStudyDate(now.value).slice(0, 7))
const calendar = computed(() => {
  const start = shiftStudyDate(currentMonth.value, -((currentMonth.value.getDay() + 6) % 7))
  const days = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() + 1, 0).getDate()
  const cells = Math.ceil((((currentMonth.value.getDay() + 6) % 7) + days) / 7) * 7
  return Array.from({ length: cells }, (_, index) => {
    const date = shiftStudyDate(start, index)
    const key = localStudyDate(date)
    return { date, key, inMonth: key.startsWith(monthPrefix.value), seconds: progress.value.daily[key] || 0 }
  })
})
function moveMonth(offset) {
  if (offset > 0 && isCurrentMonth.value) return
  currentMonth.value = new Date(currentMonth.value.getFullYear(), currentMonth.value.getMonth() + offset, 1)
}
function formatTime(seconds) {
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor(seconds % 3600 / 60)
  const rest = Math.floor(seconds % 60)
  if (hours) return `${hours}${t('checkIn.timeFormat.hours')} ${minutes}${t('checkIn.timeFormat.minutes')}`
  if (minutes) return `${minutes}${t('checkIn.timeFormat.minutes')}${rest ? ` ${rest}${t('checkIn.timeFormat.seconds')}` : ''}`
  return `${rest}${t('checkIn.timeFormat.seconds')}`
}
const lastSource = computed(() => readStudySource(user.value?.uid))
const opening = ref(false)
const openError = ref('')
// 有上次听过的内容就直接续上，没有就去素材库
async function practice() {
  if (!lastSource.value) {
    handleViewChange('Material')
    return
  }
  opening.value = true
  openError.value = ''
  try {
    const translate = (...args) => t(...args)
    translate.locale = locale.value
    const audio = await resolveStudySource(lastSource.value, translate)
    handleViewChange('AudioPlayer', {
      ...audio,
      useSmartSplit: false,
      isDemo: false,
      focusSentence: { text: lastSource.value.sentence, index: lastSource.value.subtitleIndex },
      returnView: 'CheckIn',
    })
  } catch (error) {
    if (!(error instanceof StudySourceMissingError)) throw error
    openError.value = copy.value.openFailed
  } finally {
    opening.value = false
  }
}
function refreshDate() {
  const followCurrentMonth = isCurrentMonth.value
  now.value = new Date()
  if (followCurrentMonth) currentMonth.value = new Date(now.value.getFullYear(), now.value.getMonth(), 1)
}
function subscribe() {
  unsubscribe?.()
  daily.value = {}
  error.value = false
  if (!user.value?.uid) { loading.value = false; return }
  loading.value = true
  const uid = user.value.uid
  unsubscribe = onSnapshot(doc(db, 'users', uid), snapshot => {
    if (user.value?.uid !== uid) return
    daily.value = snapshot.data()?.listeningStats || {}
    checkIn.value = snapshot.data()?.checkIn || {}
    error.value = false
    loading.value = false
  }, () => { if (user.value?.uid === uid) { error.value = true; loading.value = false } })
}
watch(() => user.value?.uid, subscribe, { immediate: true })
onMounted(() => {
  void flushListeningActivity()
  clock = setInterval(refreshDate, 30000)
  window.addEventListener('focus', refreshDate)
})
onUnmounted(() => { unsubscribe?.(); clearInterval(clock); window.removeEventListener('focus', refreshDate) })
</script>

<style scoped>
/* 一列居中；首屏是连续天数、今日进度和继续练习，往下依次是里程碑、周榜、月历 */
.checkin {
  width: min(680px, 100%);
  margin: 0 auto;
  padding: var(--space-6) 0 var(--space-12);
  color: var(--ink-primary);
}

button {
  font: inherit;
  cursor: pointer;
}

button:disabled {
  opacity: 0.35;
  cursor: default;
}

button:focus-visible {
  outline: none;
  box-shadow: var(--focus-ring-brand-strong);
}

/* 每屏唯一的主操作，胶囊形，宽度随文字 */
.primary-button {
  flex: 0 0 auto;
  height: 44px;
  padding: 0 var(--space-6);
  border: 0;
  border-radius: var(--radius-pill);
  background: var(--ink-primary);
  color: var(--color-text-inverse);
  font-size: var(--font-size-sm);
  font-weight: 600;
  white-space: nowrap;
}

.checkin-state {
  display: grid;
  justify-items: center;
  gap: var(--space-4);
  padding: var(--space-16) 0;
  color: var(--ink-secondary);
  text-align: center;
}

.checkin-state p {
  margin: 0;
}

/* 连续天数：环是今天的打卡进度，环里是连续天数 */
.streak-hero {
  display: grid;
  justify-items: center;
  text-align: center;
}

.streak-ring {
  position: relative;
  display: grid;
  place-items: center;
  align-content: center;
  width: 184px;
  height: 184px;
}

.streak-ring .ring {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.ring-track,
.ring-fill {
  fill: none;
  stroke-width: 6;
}

.ring-track {
  stroke: rgba(16, 24, 32, 0.06);
}

.ring-fill {
  stroke: var(--signal-active-strong);
  stroke-linecap: round;
  stroke-dasharray: 339.29;
  transition: stroke-dashoffset 350ms ease;
}

/* 没打卡是灰色火焰；打卡后点亮，背后一圈暖光 */
.streak-flame {
  position: relative;
  width: 30px;
  height: 38px;
  margin-bottom: var(--space-1);
}

.streak-ring::before {
  content: "";
  position: absolute;
  inset: 14px;
  border-radius: 50%;
  background: var(--streak-flame-glow);
  opacity: 0;
  transition: opacity 400ms ease;
}

.streak-ring.lit::before {
  opacity: 1;
}

.streak-ring.lit .streak-count,
.streak-unit {
  position: relative;
}

.streak-count {
  color: var(--streak-flame-outer);
}

.streak-ring.ignite .streak-count {
  animation: count-pop 700ms var(--ease-spring) 300ms both;
}

@keyframes count-pop {
  0% { transform: scale(0.6); opacity: 0; }
  60% { transform: scale(1.15); opacity: 1; }
  100% { transform: scale(1); }
}

.streak-count {
  font-family: var(--font-family-data);
  font-size: 3.5rem;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  line-height: 1;
  letter-spacing: -0.04em;
}

.streak-unit {
  margin-top: var(--space-1);
  color: var(--ink-secondary);
  font-size: var(--font-size-xs);
}

.today-status {
  margin: var(--space-4) 0 0;
  color: var(--ink-primary);
  font-size: var(--font-size-md);
  font-weight: 500;
  line-height: 1.5;
}

.today-status.complete {
  color: var(--signal-active-strong);
}

.streak-message {
  max-width: 28em;
  margin: var(--space-1) 0 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

/* 本周七天 */
.week-track {
  display: flex;
  justify-content: center;
  gap: var(--space-4);
  margin: var(--space-6) 0;
  padding: 0;
  list-style: none;
}

.week-track li {
  display: grid;
  justify-items: center;
  gap: var(--space-2);
}

.week-name {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.week-mark {
  display: grid;
  place-items: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: rgba(16, 24, 32, 0.06);
  color: var(--ink-secondary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-sm);
}

.week-mark .material-icons {
  font-size: 1.25rem;
}

.week-track .complete .week-mark {
  background: var(--signal-active-strong);
  color: var(--color-text-inverse);
}

.week-track .frozen .week-mark {
  background: var(--streak-freeze-bg);
  color: var(--streak-freeze);
}

.week-track .today .week-mark {
  box-shadow: 0 0 0 2px var(--color-page), 0 0 0 4px var(--ink-primary);
}

.week-track .today .week-name {
  color: var(--ink-primary);
  font-weight: 600;
}

.week-track .future .week-mark {
  background: transparent;
  color: var(--ink-tertiary);
}

/* 面板与面板下的小字 */
.panel {
  padding: var(--space-5);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  background: var(--surface-panel);
}

.footnote {
  margin: var(--space-4) var(--space-5) 0;
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  line-height: 1.6;
}

.panel-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
}

.panel h2 {
  margin: 0;
  font-size: var(--font-size-md);
  font-weight: 600;
  line-height: 1.4;
}

.panel-meta {
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
}

/* 继续练习：有上次听过的内容就写出名字，太长截断 */
.practice-action {
  display: grid;
  justify-items: center;
  gap: var(--space-2);
  margin-bottom: var(--space-10);
}

.practice-button {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  max-width: 100%;
}

.practice-button .material-icons {
  font-size: 20px;
}

.practice-label {
  overflow: hidden;
  text-overflow: ellipsis;
}

.practice-error {
  margin: 0;
  color: var(--color-danger);
  font-size: var(--font-size-sm);
}

.milestones {
  margin-bottom: var(--space-6);
}

.milestone-next {
  margin: var(--space-3) 0 0;
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
  line-height: 1.6;
}

/* 冻结卡：月历摘要里的两个雪花格子，持有的点亮 */
.freeze-held {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
}

.freeze-slots {
  display: inline-flex;
  gap: 2px;
}

.freeze-slots .material-icons {
  color: rgba(16, 24, 32, 0.16);
  font-size: 18px;
}

.freeze-slots .held {
  color: var(--streak-freeze);
}

.calendar {
  margin-top: var(--space-6);
}

.meter {
  height: 4px;
  margin-top: var(--space-4);
  overflow: hidden;
  border-radius: var(--radius-pill);
  background: rgba(16, 24, 32, 0.06);
}

.meter > span {
  display: block;
  height: 100%;
  border-radius: inherit;
  background: var(--signal-active-strong);
  transition: width 350ms ease;
}

/* 月历：只有圆点，不画格线 */
.month-nav {
  display: flex;
  gap: var(--space-1);
}

.icon-button {
  display: grid;
  place-items: center;
  width: 32px;
  height: 32px;
  padding: 0;
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--ink-secondary);
}

.icon-button:hover:not(:disabled) {
  background: var(--surface-subtle);
  color: var(--ink-primary);
}

.month-summary {
  display: flex;
  flex-wrap: wrap;
  gap: var(--space-1) var(--space-5);
  margin: var(--space-1) 0 var(--space-4);
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
}

.month-summary strong {
  color: var(--ink-primary);
  font-weight: 500;
}

.calendar-grid {
  display: grid;
  grid-template-columns: repeat(7, minmax(0, 1fr));
  justify-items: center;
  row-gap: var(--space-2);
}

.calendar-weekdays {
  margin-bottom: var(--space-2);
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
}

.calendar-day {
  display: grid;
  place-items: center;
  width: 36px;
  height: 36px;
  padding: 0;
  border: 0;
  border-radius: 50%;
  background: transparent;
  color: var(--ink-secondary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-sm);
}

.calendar-day:disabled {
  opacity: 1;
  color: var(--ink-tertiary);
}

.calendar-day.outside {
  visibility: hidden;
}

/* 热力图：听得越久颜色越深 */
.heat-1 { background: color-mix(in srgb, var(--signal-active-strong) 10%, var(--surface-panel)); }
.heat-2 { background: color-mix(in srgb, var(--signal-active-strong) 28%, var(--surface-panel)); color: var(--ink-primary); }
.heat-3 { background: color-mix(in srgb, var(--signal-active-strong) 60%, var(--surface-panel)); color: var(--color-text-inverse); }
.heat-4 { background: var(--signal-active-strong); color: var(--color-text-inverse); }

.calendar-day.frozen {
  background: var(--streak-freeze-bg);
  color: var(--streak-freeze);
}

.calendar-day.today {
  font-weight: 700;
  text-decoration: underline;
  text-underline-offset: 3px;
}

.calendar-day.selected {
  box-shadow: 0 0 0 2px var(--surface-panel), 0 0 0 3.5px var(--ink-primary);
}

.day-detail {
  display: flex;
  align-items: baseline;
  gap: var(--space-3);
  margin: var(--space-4) 0 0;
  padding-top: var(--space-4);
  border-top: 1px solid var(--border-subtle);
  color: var(--ink-secondary);
  font-size: var(--font-size-sm);
}

.day-detail strong {
  color: var(--ink-primary);
  font-family: var(--font-family-data);
  font-weight: 500;
}

.day-checked {
  color: var(--signal-active-strong);
}

.day-frozen {
  color: var(--streak-freeze);
}

.badge-reward {
  color: var(--ink-tertiary);
  font-family: var(--font-family-data);
  font-size: var(--font-size-xs);
}

.badges .earned .badge-reward {
  color: var(--signal-active-strong);
}

/* 里程碑 */
.badges {
  display: grid;
  grid-template-columns: repeat(6, minmax(0, 1fr));
  gap: var(--space-2);
  margin: var(--space-5) 0 0;
  padding: 0;
  list-style: none;
}

.badges li {
  display: grid;
  justify-items: center;
  gap: var(--space-2);
  text-align: center;
}

/* 徽章图案下方压一枚天数小标签 */
.badge-art {
  position: relative;
  display: block;
  width: 56px;
  height: 63px;
  margin-bottom: var(--space-4);
}

.badge-art svg {
  width: 100%;
  height: 100%;
}

.badge-days {
  position: absolute;
  bottom: -12px;
  left: 50%;
  padding: 0 var(--space-2);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-pill);
  background: var(--surface-panel);
  color: var(--ink-secondary);
  font-family: var(--font-family-data);
  font-size: 11px;
  line-height: 18px;
  white-space: nowrap;
  transform: translateX(-50%);
}

.badges .earned .badge-days {
  color: var(--ink-primary);
  font-weight: 600;
}

.badge-name {
  color: var(--ink-tertiary);
  font-size: var(--font-size-xs);
  line-height: 1.4;
}

.badges .earned .badge-name {
  color: var(--ink-primary);
}

@media (prefers-reduced-motion: reduce) {
  .meter > span,
  .ring-fill,
  .streak-ring::before {
    transition: none;
  }

  .streak-ring.ignite .streak-count {
    animation: none;
  }
}

@media (max-width: 640px) {
  .week-track {
    gap: var(--space-2);
  }

  .week-mark {
    width: 36px;
    height: 36px;
  }

  .badges {
    grid-template-columns: repeat(3, minmax(0, 1fr));
    row-gap: var(--space-4);
  }
}
</style>
